import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TrendingUp, Calendar, Flame, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { todayStartISO, todayEndISO, in60DaysISO, formatAED, relativeTime, initials, formatDateTime } from '@/lib/format';
import { STAGES, ZONES, STAGE_COLOR, DEAL_TYPES, DEAL_TYPE_COLOR } from '@/lib/constants';
import { PriorityBadge, SourceBadge, StageBadge } from '@/components/pipeline/Badges';

export default function DashboardPage() {
    const { profile } = useAuth();
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [activities, setActivities] = useState([]);
    const [agents, setAgents] = useState([]);
    const [liveLeads, setLiveLeads] = useState([]);
    const [pulseId, setPulseId] = useState(null);

    const load = useCallback(async () => {
        if (!profile?.organization_id) return;
        let cq = supabase.from('pipeline_contacts').select('*').is('deleted_at', null).order('created_at', { ascending: false });
        if (profile.role !== 'admin') cq = cq.eq('assigned_to', profile.id);
        const [{ data: { user: authUser } }, { count: totalCount }, { data: cs }, { data: acts }, { data: ag }] = await Promise.all([
            supabase.auth.getUser(),
            supabase.from('pipeline_contacts').select('*', { count: 'exact', head: true }).is('deleted_at', null),
            cq,
            supabase.from('pipeline_activities').select('*').order('created_at', { ascending: false }).limit(15),
            supabase.from('profiles').select('id, full_name, email, role, is_active'),
        ]);
        console.log('[Dashboard] auth user id:', authUser?.id);
        console.log('[Dashboard] profile id:', profile?.id);
        console.log('[Dashboard] profile role:', profile?.role);
        console.log('[Dashboard] contacts (unfiltered):', totalCount, '| contacts (after filter):', cs?.length ?? 0);
        setContacts(cs || []);
        setActivities(acts || []);
        setAgents(ag || []);
        setLiveLeads((cs || []).slice(0, 8));
    }, [profile?.organization_id, profile?.role, profile?.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!profile?.organization_id) return;
        const ch = supabase
            .channel(`dash-pc-${profile.organization_id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'pipeline_contacts', filter: `organization_id=eq.${profile.organization_id}` }, (payload) => {
                const c = payload.new;
                if (profile.role !== 'admin' && c.assigned_to !== profile.id) return;
                setContacts((prev) => [c, ...prev]);
                setLiveLeads((prev) => [c, ...prev].slice(0, 8));
                setPulseId(c.id);
                setTimeout(() => setPulseId(null), 3000);
            })
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'pipeline_activities', filter: `organization_id=eq.${profile.organization_id}` }, (payload) => {
                setActivities((prev) => [payload.new, ...prev].slice(0, 15));
            })
            .subscribe();
        return () => { supabase.removeChannel(ch); };
    }, [profile?.organization_id]);

    const kpis = useMemo(() => {
        const inactive = ['Closed Lost', 'Re-engage Pool'];
        const inPipeline = contacts.filter((c) => !inactive.includes(c.pipeline_stage)).length;
        const today = new Date(); today.setHours(0,0,0,0);
        const tomorrow = new Date(); tomorrow.setHours(23,59,59,999);
        const followToday = contacts.filter((c) => c.next_followup_at && new Date(c.next_followup_at) >= today && new Date(c.next_followup_at) <= tomorrow).length;
        const hot = contacts.filter((c) => c.priority === 'Hot' && !inactive.includes(c.pipeline_stage)).length;
        const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
        const closedMonth = contacts.filter((c) => c.pipeline_stage === 'Closed Won' && new Date(c.updated_at || c.created_at) >= monthStart).length;
        const renewals = contacts.filter((c) => c.lease_end_date && new Date(c.lease_end_date) <= new Date(in60DaysISO())).length;
        return { inPipeline, followToday, hot, closedMonth, renewals };
    }, [contacts]);

    const dealTypeCounts = useMemo(() => {
        const counts = Object.fromEntries(DEAL_TYPES.map((d) => [d, 0]));
        for (const c of contacts) if (c.deal_type) counts[c.deal_type] = (counts[c.deal_type] || 0) + 1;
        return counts;
    }, [contacts]);

    const funnel = useMemo(() => {
        const totals = Object.fromEntries(STAGES.map((s) => [s, 0]));
        for (const c of contacts) totals[c.pipeline_stage] = (totals[c.pipeline_stage] || 0) + 1;
        return totals;
    }, [contacts]);

    const todayFollowUps = useMemo(() => {
        const now = new Date();
        return contacts
            .filter((c) => {
                if (!c.next_followup_at) return false;
                const d = new Date(c.next_followup_at);
                return d.toDateString() === now.toDateString() || d < now;
            })
            .sort((a, b) => new Date(a.next_followup_at) - new Date(b.next_followup_at))
            .slice(0, 8);
    }, [contacts]);

    return (
        <div className="px-4 lg:px-6 py-6 space-y-6 max-w-[1600px] mx-auto" data-testid="dashboard-page">
            <header className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-aks-text">Welcome back{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}</h1>
                    <p className="text-[13px] text-gray-500 mt-1">Your pipeline at a glance</p>
                </div>
                <div className="hidden md:flex items-center gap-2">
                    <button onClick={() => navigate('/pipeline')} className="h-9 px-3.5 rounded-md border border-gray-300 hover:border-aks-gold text-[13px] font-medium">Open pipeline</button>
                </div>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KpiCard label="In pipeline" value={kpis.inPipeline} icon={<TrendingUp className="h-4 w-4" />} accent="#0A1628" onClick={() => navigate('/pipeline')} testId="kpi-in-pipeline" />
                <KpiCard label="Follow-ups today" value={kpis.followToday} icon={<Calendar className="h-4 w-4" />} accent="#F59E0B" onClick={() => navigate('/pipeline?view=followup')} testId="kpi-followups-today" />
                <KpiCard label="Hot prospects" value={kpis.hot} icon={<Flame className="h-4 w-4" />} accent="#EF4444" onClick={() => navigate('/pipeline?priority=Hot')} testId="kpi-hot" />
                <KpiCard label="Closed this month" value={kpis.closedMonth} icon={<CheckCircle2 className="h-4 w-4" />} accent="#10B981" onClick={() => navigate('/pipeline?stage=Closed%20Won')} testId="kpi-closed-month" />
                <KpiCard label="Renewals (60d)" value={kpis.renewals} icon={<AlertTriangle className="h-4 w-4" />} accent="#C9A84C" onClick={() => navigate('/pipeline?stage=Renewal%20Due')} testId="kpi-renewals" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-[15px] font-bold text-aks-text">Pipeline funnel</h2>
                        <span className="text-[11px] text-gray-500">Click a stage to filter</span>
                    </div>
                    <div className="space-y-3">
                        {ZONES.map((z) => (
                            <div key={z.key}>
                                <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: z.color }}>{z.label}</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {z.stages.map((s) => {
                                        const count = funnel[s] || 0;
                                        const c = STAGE_COLOR[s];
                                        return (
                                            <button key={s} onClick={() => navigate(`/pipeline?stage=${encodeURIComponent(s)}`)} className="flex items-center gap-2 h-8 px-2.5 rounded-md border bg-white hover:shadow-sm transition" style={{ borderColor: `${c}33` }}>
                                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
                                                <span className="text-[12px] font-medium text-aks-text">{s}</span>
                                                <span className="text-[11px] font-semibold" style={{ color: c }}>{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <h2 className="text-[15px] font-bold text-aks-text mb-3">Today's follow-ups</h2>
                    <div className="space-y-2 max-h-[320px] overflow-y-auto">
                        {todayFollowUps.length === 0 ? (
                            <div className="py-8 text-center text-[12px] text-gray-400">No follow-ups due</div>
                        ) : todayFollowUps.map((c) => (
                            <div key={c.id} onClick={() => navigate(`/pipeline/${c.id}`)} className="p-2.5 rounded-md border border-gray-200 hover:border-aks-gold/60 cursor-pointer" data-testid={`today-fu-${c.id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold text-[13px] text-aks-text truncate">{c.full_name}</span>
                                    <PriorityBadge value={c.priority} />
                                </div>
                                <div className="text-[11px] text-gray-500 mt-0.5">{formatDateTime(c.next_followup_at)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h2 className="text-[15px] font-bold text-aks-text mb-4">Leads by deal type</h2>
                <div className="grid grid-cols-3 gap-3">
                    {DEAL_TYPES.map((dt) => {
                        const c = DEAL_TYPE_COLOR[dt];
                        const count = dealTypeCounts[dt] || 0;
                        return (
                            <button key={dt} onClick={() => navigate(`/pipeline?dealType=${encodeURIComponent(dt)}`)} className="flex flex-col items-center justify-center gap-1 p-4 rounded-xl border hover:shadow-sm transition" style={{ borderColor: `${c}33`, background: `${c}0a` }}>
                                <span className="text-3xl font-bold" style={{ color: c }}>{count}</span>
                                <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: c }}>{dt}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="live-leads-panel">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-aks-gold opacity-60" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-aks-gold" />
                            </span>
                            <h2 className="text-[15px] font-bold text-aks-text">Live leads</h2>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-gray-500">Real-time</span>
                    </div>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {liveLeads.length === 0 ? (
                            <div className="py-10 text-center text-[12px] text-gray-400">Newest leads will appear here</div>
                        ) : liveLeads.map((c) => (
                            <div key={c.id} onClick={() => navigate(`/pipeline/${c.id}`)} className={`p-3 rounded-md border border-gray-200 hover:border-aks-gold/60 cursor-pointer aks-fade-in ${pulseId === c.id ? 'aks-pulse border-aks-gold' : ''}`} data-testid={`live-lead-${c.id}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-semibold text-[13px] text-aks-text truncate">{c.full_name}</span>
                                    <span className="text-[10px] text-gray-500">{relativeTime(c.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <SourceBadge value={c.source} detail={c.source_detail} />
                                    <span className="text-[11px] text-gray-500">{c.phone}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="recent-activity">
                    <h2 className="text-[15px] font-bold text-aks-text mb-3">Recent activity</h2>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {activities.length === 0 ? (
                            <div className="py-10 text-center text-[12px] text-gray-400">No activity yet</div>
                        ) : activities.map((a) => (
                            <div key={a.id} className="flex gap-3 py-2 border-b border-gray-50 last:border-b-0">
                                <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-600 flex-shrink-0 uppercase">
                                    {a.activity_type.slice(0, 2)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12px] text-aks-text">{a.description}</div>
                                    <div className="text-[10px] text-gray-400 mt-0.5">{relativeTime(a.created_at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function KpiCard({ label, value, icon, accent, onClick, testId }) {
    return (
        <button onClick={onClick} className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-aks-gold/40 transition" data-testid={testId}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
                <span className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>{icon}</span>
            </div>
            <div className="mt-2 text-3xl font-bold tracking-tight" style={{ color: accent }}>{value}</div>
        </button>
    );
}
